import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Category {
  id: number;
  name: string;
  description: string;
  sort_order: number;
  parent_id: number | null;
}

interface Topic {
  id: number;
  category_id: number;
  category_name: string;
  title: string;
  content: string;
  author_id: string | null;
  author_name: string;
  author_avatar_url?: string | null;
  author_default_avatar?: number | null;
  view_count: number;
  reply_count: number;
  is_pinned: boolean;
  is_locked: boolean;
  is_deleted: boolean;
  created_at: string;
  last_reply_at: string | null;
}

interface Reply {
  id: number;
  topic_id: number;
  content: string;
  author_id: string | null;
  author_name: string;
  author_avatar_url?: string | null;
  author_default_avatar?: number | null;
  is_deleted: boolean;
  created_at: string;
}

interface TopicsResponse {
  topics: Topic[];
  total: number;
  page: number;
  limit: number;
}

interface TopicDetailResponse {
  topic: Topic;
  replies: Reply[];
}

export function useCategories() {
  return useQuery({
    queryKey: ['forum', 'categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('forum_categories')
        .select('*')
        .order('sort_order');
      
      if (error) throw error;
      return data as Category[];
    },
  });
}

export function useSearchTopics(searchQuery: string) {
  const limit = 20;
  
  return useQuery({
    queryKey: ['forum', 'search', searchQuery],
    queryFn: async (): Promise<TopicsResponse> => {
      const searchTerm = `%${searchQuery.toLowerCase()}%`;
      
      const { data, error, count } = await supabase
        .from('forum_topics')
        .select(`
          *,
          forum_categories!inner(name)
        `, { count: 'exact' })
        .or(`title.ilike.${searchTerm},content.ilike.${searchTerm}`)
        .order('last_reply_at', { ascending: false, nullsFirst: false })
        .limit(limit);

      if (error) throw error;

      // Fetch profiles for authors
      const authorIds = [...new Set((data || []).map(t => t.author_id).filter(Boolean))] as string[];
      let profilesMap: Record<string, { avatar_url: string | null; default_avatar: number | null }> = {};
      
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, avatar_url, default_avatar')
          .in('id', authorIds);
        
        profilesMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = { avatar_url: p.avatar_url, default_avatar: p.default_avatar };
          return acc;
        }, {} as Record<string, { avatar_url: string | null; default_avatar: number | null }>);
      }

      const topics = (data || []).map(t => ({
        ...t,
        category_name: (t.forum_categories as any)?.name || '',
        author_avatar_url: t.author_id ? profilesMap[t.author_id]?.avatar_url || null : null,
        author_default_avatar: t.author_id ? profilesMap[t.author_id]?.default_avatar || null : null,
      }));

      return {
        topics,
        total: count || 0,
        page: 1,
        limit,
      };
    },
    enabled: searchQuery.length >= 2,
  });
}

export function useTopics(categoryId?: number, page = 1, allCategories?: Category[]) {
  const limit = 20;
  
  return useQuery({
    queryKey: ['forum', 'topics', categoryId, page],
    queryFn: async (): Promise<TopicsResponse> => {
      // Determine which category IDs to include
      let categoryIds: number[] = [];
      
      if (categoryId && allCategories) {
        // Check if selected category is a parent (has children)
        const childCategories = allCategories.filter(c => c.parent_id === categoryId);
        if (childCategories.length > 0) {
          // Include parent and all children
          categoryIds = [categoryId, ...childCategories.map(c => c.id)];
        } else {
          categoryIds = [categoryId];
        }
      }
      
      let query = supabase
        .from('forum_topics')
        .select(`
          *,
          forum_categories!inner(name)
        `, { count: 'exact' });

      if (categoryIds.length > 0) {
        query = query.in('category_id', categoryIds);
      }

      // Order: pinned first, then by last_reply_at or created_at
      query = query
        .order('is_pinned', { ascending: false })
        .order('last_reply_at', { ascending: false, nullsFirst: false })
        .range((page - 1) * limit, page * limit - 1);

      const { data, error, count } = await query;

      if (error) throw error;

      // Fetch profiles for authors separately
      const authorIds = [...new Set((data || []).map(t => t.author_id).filter(Boolean))] as string[];
      let profilesMap: Record<string, { avatar_url: string | null; default_avatar: number | null }> = {};
      
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, avatar_url, default_avatar')
          .in('id', authorIds);
        
        profilesMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = { avatar_url: p.avatar_url, default_avatar: p.default_avatar };
          return acc;
        }, {} as Record<string, { avatar_url: string | null; default_avatar: number | null }>);
      }

      const topics = (data || []).map(t => ({
        ...t,
        category_name: (t.forum_categories as any)?.name || '',
        author_avatar_url: t.author_id ? profilesMap[t.author_id]?.avatar_url || null : null,
        author_default_avatar: t.author_id ? profilesMap[t.author_id]?.default_avatar || null : null,
      }));

      return {
        topics,
        total: count || 0,
        page,
        limit,
      };
    },
  });
}

export function useTopic(topicId: number, incrementViews = true) {
  return useQuery({
    queryKey: ['forum', 'topic', topicId],
    queryFn: async (): Promise<TopicDetailResponse> => {
      // Fetch topic with category
      const { data: topic, error: topicError } = await supabase
        .from('forum_topics')
        .select(`
          *,
          forum_categories!inner(name)
        `)
        .eq('id', topicId)
        .single();

      if (topicError) throw topicError;

      // Increment view count after fetching
      if (incrementViews && topic) {
        await supabase
          .from('forum_topics')
          .update({ view_count: (topic.view_count || 0) + 1 })
          .eq('id', topicId);
      }

      // Fetch replies
      const { data: replies, error: repliesError } = await supabase
        .from('forum_replies')
        .select('*')
        .eq('topic_id', topicId)
        .order('created_at');

      if (repliesError) throw repliesError;

      // Fetch profiles for all authors
      const allAuthorIds = [
        topic.author_id,
        ...(replies || []).map(r => r.author_id)
      ].filter(Boolean) as string[];
      
      const uniqueAuthorIds = [...new Set(allAuthorIds)];
      let profilesMap: Record<string, { avatar_url: string | null; default_avatar: number | null }> = {};
      
      if (uniqueAuthorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, avatar_url, default_avatar')
          .in('id', uniqueAuthorIds);
        
        profilesMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = { avatar_url: p.avatar_url, default_avatar: p.default_avatar };
          return acc;
        }, {} as Record<string, { avatar_url: string | null; default_avatar: number | null }>);
      }

      return {
        topic: {
          ...topic,
          category_name: (topic.forum_categories as any)?.name || '',
          author_avatar_url: topic.author_id ? profilesMap[topic.author_id]?.avatar_url || null : null,
          author_default_avatar: topic.author_id ? profilesMap[topic.author_id]?.default_avatar || null : null,
        },
        replies: (replies || []).map(r => ({
          ...r,
          author_avatar_url: r.author_id ? profilesMap[r.author_id]?.avatar_url || null : null,
          author_default_avatar: r.author_id ? profilesMap[r.author_id]?.default_avatar || null : null,
        })),
      };
    },
    enabled: !!topicId,
  });
}

export function useCreateTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { 
      categoryId: number; 
      title: string; 
      content: string; 
      authorName: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('forum_topics')
        .insert({
          category_id: params.categoryId,
          title: params.title,
          content: params.content,
          author_id: user?.id || null,
          author_name: params.authorName,
        })
        .select('id')
        .single();

      if (error) throw error;
      return { success: true, topicId: data.id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

export function useCreateReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { 
      topicId: number; 
      content: string; 
      authorName: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('forum_replies')
        .insert({
          topic_id: params.topicId,
          content: params.content,
          author_id: user?.id || null,
          author_name: params.authorName,
        })
        .select('id')
        .single();

      if (error) throw error;

      // Update topic reply count and last_reply_at
      const { data: topic } = await supabase
        .from('forum_topics')
        .select('reply_count')
        .eq('id', params.topicId)
        .single();

      await supabase
        .from('forum_topics')
        .update({ 
          reply_count: (topic?.reply_count || 0) + 1,
          last_reply_at: new Date().toISOString(),
        })
        .eq('id', params.topicId);

      return { success: true, replyId: data.id };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topic', variables.topicId] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

// ==================== MODERATION HOOKS ====================

export function useDeleteTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (topicId: number) => {
      const { error } = await supabase
        .from('forum_topics')
        .delete()
        .eq('id', topicId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

// Soft delete for users - marks as deleted instead of removing
export function useSoftDeleteTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (topicId: number) => {
      const { error } = await supabase
        .from('forum_topics')
        .update({ 
          is_deleted: true,
          content: '[Inlägget har tagits bort av användaren]'
        })
        .eq('id', topicId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: (_, topicId) => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topic', topicId] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

export function useSoftDeleteReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { replyId: number; topicId: number }) => {
      const { error } = await supabase
        .from('forum_replies')
        .update({ 
          is_deleted: true,
          content: '[Svaret har tagits bort av användaren]'
        })
        .eq('id', params.replyId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topic', variables.topicId] });
    },
  });
}

export function useDeleteReply() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { replyId: number; topicId: number }) => {
      const { error } = await supabase
        .from('forum_replies')
        .delete()
        .eq('id', params.replyId);

      if (error) throw error;

      // Update reply count manually
      const { data: topic } = await supabase
        .from('forum_topics')
        .select('reply_count')
        .eq('id', params.topicId)
        .single();

      if (topic) {
        await supabase
          .from('forum_topics')
          .update({ reply_count: Math.max(0, (topic.reply_count || 1) - 1) })
          .eq('id', params.topicId);
      }

      return { success: true };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topic', variables.topicId] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

export function useToggleLockTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (topicId: number) => {
      const { data: topic } = await supabase
        .from('forum_topics')
        .select('is_locked')
        .eq('id', topicId)
        .single();

      const { error } = await supabase
        .from('forum_topics')
        .update({ is_locked: !topic?.is_locked })
        .eq('id', topicId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: (_, topicId) => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topic', topicId] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

export function useTogglePinTopic() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (topicId: number) => {
      const { data: topic } = await supabase
        .from('forum_topics')
        .select('is_pinned')
        .eq('id', topicId)
        .single();

      const { error } = await supabase
        .from('forum_topics')
        .update({ is_pinned: !topic?.is_pinned })
        .eq('id', topicId);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: (_, topicId) => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'topic', topicId] });
      queryClient.invalidateQueries({ queryKey: ['forum', 'topics'] });
    },
  });
}

interface Moderator {
  id: string;
  email: string;
  display_name: string;
  role: string;
  role_assigned_at: string;
}

export function useModerators() {
  return useQuery({
    queryKey: ['forum', 'moderators'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          created_at,
          profiles!inner(display_name)
        `)
        .in('role', ['admin', 'moderator']);

      if (error) throw error;

      return (data || []).map(r => ({
        id: r.user_id,
        display_name: (r.profiles as any)?.display_name || 'Okänd',
        role: r.role,
        role_assigned_at: r.created_at,
      })) as Moderator[];
    },
  });
}

export function useAddModerator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.rpc('add_moderator_by_email', {
        target_email: email
      });

      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'moderators'] });
    },
  });
}

export function useRemoveModerator() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc('remove_moderator', {
        target_user_id: userId
      });

      if (error) throw new Error(error.message);
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['forum', 'moderators'] });
    },
  });
}
